import {
  AccountId,
  ContractId,
  ContractFunctionParameters,
} from "@hashgraph/sdk";
import { Button, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { ContractFunctionParameterBuilder } from "../services/wallets/contractFunctionParameterBuilder";
import { useWalletInterface } from "../services/wallets/useWalletInterface";
import { useEffect, useState } from "react";

import {
  getTokenBalance,
  isAssociatedWithToken,
  getDepositedBalance,
  getTokenAllowance,
} from "../services/mirrorUtils";

// --- CONFIGURATION ---
const CUTOFF_DATE = new Date("2025-11-22T04:00:00Z"); // <-- ADJUST THIS DATE FOR TESTING

const usdcTokenId = "0.0.456858";
const tdrillTokenId = "0.0.9484420";
const contractId = "0.0.10079751";
const allowanceLearnMoreUrl = "https://tozm.io/?month11";

// Toggle whether to show the drill dApp UI below the December section
const SHOW_DRILL_APP = false;

// Mirror Node base URL – adjust if/when you move to mainnet
const MIRROR_BASE_URL = "https://testnet.mirrornode.hedera.com";

export default function Home() {
  const { walletInterface, accountId } = useWalletInterface();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false); // Used for withdraw success
  const [depositSuccess, setDepositSuccess] = useState(false); // Used for deposit success

  // --- State for both modes ---
  const [isDepositPhase, setIsDepositPhase] = useState(
    new Date() < CUTOFF_DATE,
  );

  // --- State for Withdraw Mode ---
  const [contractHasTdrill, setContractHasTdrill] = useState(false);
  const [userDepositedBalance, setUserDepositedBalance] = useState<bigint>(
    BigInt(0),
  );
  const [isTdrillAssociated, setIsTdrillAssociated] = useState(false);

  // --- State for Deposit Mode ---
  const [isUsdcAssociated, setIsUsdcAssociated] = useState(false);
  const [userTdrillBalance, setUserTdrillBalance] = useState(0);
  const [allowance, setAllowance] = useState(0);
  const [depositAmount, setDepositAmount] = useState("");

  // --- Referrer input state (from URL and user typing) ---
  const [referrerInput, setReferrerInput] = useState("");
  const [normalizedReferrerId, setNormalizedReferrerId] = useState<string | null>(null);
  const [referrerError, setReferrerError] = useState<string | null>(null);
  const [isMirrorChecking, setIsMirrorChecking] = useState(false);
  const [isValidMirrorAccount, setIsValidMirrorAccount] =
    useState<boolean | null>(null);
  const [evmAddress, setEvmAddress] = useState<string | null>(null);

  // Helper to fetch account info from Mirror Node
  const fetchAccountFromMirror = async (accountIdStr: string) => {
    try {
      const res = await fetch(
        `${MIRROR_BASE_URL}/api/v1/accounts/${accountIdStr}`,
      );
      if (!res.ok) {
        // 404 or other error – treat as "not found"
        return null;
      }
      const data = await res.json();
      return data;
    } catch (err) {
      console.error("Error fetching account from Mirror Node:", err);
      return null;
    }
  };

  // --- useEffect #1: read ?id= from URL and pre-populate input ---
  useEffect(() => {
    if (typeof window === "undefined") return;

    const searchParams = new URLSearchParams(window.location.search);
    const rawId = searchParams.get("id") || "";

    if (rawId) {
      setReferrerInput(rawId);
    }
  }, []);

  // --- useEffect #2: validate & Mirror-check whenever input changes ---
  useEffect(() => {
    // If input is empty, clear all derived state
    if (!referrerInput) {
      setNormalizedReferrerId(null);
      setReferrerError(null);
      setIsMirrorChecking(false);
      setIsValidMirrorAccount(null);
      setEvmAddress(null);
      return;
    }

    // Basic syntax: only digits and periods
    if (!/^[0-9.]+$/.test(referrerInput)) {
      setNormalizedReferrerId(null);
      setReferrerError("ID must contain only digits and periods.");
      setIsMirrorChecking(false);
      setIsValidMirrorAccount(false);
      setEvmAddress(null);
      return;
    }

    // Try parsing with SDK to confirm it’s a valid Hedera account format
    let parsedAccount: AccountId;
    try {
      parsedAccount = AccountId.fromString(referrerInput);
    } catch (e) {
      console.error("Invalid Hedera account string:", e);
      setNormalizedReferrerId(null);
      setReferrerError("Not a valid Hedera account format (e.g., 0.0.12345).");
      setIsMirrorChecking(false);
      setIsValidMirrorAccount(false);
      setEvmAddress(null);
      return;
    }

    const normalized = parsedAccount.toString(); // e.g., "0.0.12345"
    setNormalizedReferrerId(normalized);
    setReferrerError(null);
    setIsMirrorChecking(true);
    setIsValidMirrorAccount(null);
    setEvmAddress(null);

    let cancelled = false;

    (async () => {
      const mirrorData = await fetchAccountFromMirror(normalized);

      if (cancelled) return;

      if (!mirrorData) {
        // Not found on Mirror Node
        setIsValidMirrorAccount(false);

        // Still compute the theoretical EVM address from the AccountId
        const solidityAddr = parsedAccount.toSolidityAddress(); // 40 hex chars
        setEvmAddress(`0x${solidityAddr}`);

        setIsMirrorChecking(false);
        return;
      }

      // Found on Mirror Node – it’s a valid account
      setIsValidMirrorAccount(true);

      // Prefer evm_address from Mirror, fall back to SDK computed
      const mirrorEvm = mirrorData.evm_address as string | undefined;
      if (
        mirrorEvm &&
        mirrorEvm !== "0x0000000000000000000000000000000000000000"
      ) {
        setEvmAddress(mirrorEvm);
      } else {
        const solidityAddr = parsedAccount.toSolidityAddress();
        setEvmAddress(`0x${solidityAddr}`);
      }

      setIsMirrorChecking(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [referrerInput]);

  // --- Drill dApp status (kept for later, gated by SHOW_DRILL_APP) ---
  const checkStatus = async () => {
    if (!accountId) return;

    setLoading(true);
    const inDepositPhase = new Date() < CUTOFF_DATE;
    setIsDepositPhase(inDepositPhase);

    try {
      if (inDepositPhase) {
        const userAccountId = accountId.toString();

        // 1. Check for USDC association
        const usdcAssociated = await isAssociatedWithToken(
          userAccountId,
          usdcTokenId,
        );
        setIsUsdcAssociated(usdcAssociated);

        // 2. Check user's Tdrill wallet balance
        const tdrillBalance = await getTokenBalance(
          userAccountId,
          tdrillTokenId,
        );
        setUserTdrillBalance(tdrillBalance);

        // 3. Check Tdrill allowance given to the contract
        const tdrillAllowance = await getTokenAllowance(
          userAccountId,
          contractId,
          tdrillTokenId,
        );
        setAllowance(tdrillAllowance);
      } else {
        // --- WITHDRAW PHASE CHECKS ---
        const tdrillAssociated = await isAssociatedWithToken(
          accountId.toString(),
          tdrillTokenId,
        );
        setIsTdrillAssociated(tdrillAssociated);

        const deposited = await getDepositedBalance(
          contractId,
          AccountId.fromString(accountId),
        );
        setUserDepositedBalance(deposited);

        const contractTdrill = await getTokenBalance(contractId, tdrillTokenId);
        setContractHasTdrill(contractTdrill > 0);
      }
    } catch (err) {
      console.error("Error checking status:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!SHOW_DRILL_APP) return;
    if (!accountId) return;
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const handleWithdraw = async () => {
    if (!walletInterface) return;

    setLoading(true);
    const params = new ContractFunctionParameterBuilder(); // No parameters
    const gasLimit = 135_000;

    try {
      const txId = await walletInterface.executeContractFunction(
        ContractId.fromString(contractId),
        "withdrawDrill",
        params,
        gasLimit,
      );
      console.log("Withdraw transaction submitted:", txId?.toString());
      setSuccess(true);
    } catch (err) {
      console.error("Withdraw call failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!walletInterface || !depositAmount) return;

    const amount = Number(depositAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      console.error("Invalid deposit amount");
      return;
    }
    if (amount > allowance) {
      console.error("Amount exceeds allowance");
      return;
    }
    if (amount > userTdrillBalance) {
      console.error("Amount exceeds your wallet balance");
      return;
    }

    setLoading(true);

    const hapiParams = new ContractFunctionParameters();
    hapiParams.addUint64(amount);

    const params = {
      buildHAPIParams: () => hapiParams,
    } as ContractFunctionParameterBuilder;

    const gasLimit = 135_000;

    try {
      const txId = await walletInterface.executeContractFunction(
        ContractId.fromString(contractId),
        "depositDrillToken",
        params,
        gasLimit,
      );
      console.log("Deposit transaction submitted:", txId?.toString());
      setDepositSuccess(true);
      setDepositAmount("");
      if (SHOW_DRILL_APP) {
        checkStatus();
      }
    } catch (err) {
      console.error("Deposit call failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const getDepositButtonDisabled = () => {
    if (loading) return true;
    if (!depositAmount) return true;
    const amount = Number(depositAmount);
    if (!Number.isInteger(amount) || amount <= 0) return true;
    if (amount > allowance) return true;
    if (amount > userTdrillBalance) return true;
    return false;
  };

  const getDepositErrorMessage = () => {
    const amount = Number(depositAmount);
    if (depositAmount && (!Number.isInteger(amount) || amount <= 0)) {
      return "Amount must be a positive whole number.";
    }
    if (amount > userTdrillBalance) {
      return `Amount exceeds your balance of ${userTdrillBalance} Tdrill.`;
    }
    if (amount > allowance) {
      return `Amount exceeds your allowance of ${allowance} Tdrill.`;
    }
    return null;
  };

  const depositError = getDepositErrorMessage();

  // --- RENDER ---
  return (
    <Stack alignItems="center" spacing={4} p={4} maxWidth={600} mx="auto">
      {/* December 2025 header */}
      <Typography variant="h5" color="white" align="center">
        December 2025
      </Typography>

      {/* Referrer input */}
      <TextField
        fullWidth
        variant="outlined"
        label="Referred by (Hedera Account ID)"
        value={referrerInput}
        onChange={(e) => setReferrerInput(e.target.value)}
        InputLabelProps={{
          style: { color: "#fff" },
        }}
        InputProps={{
          style: { color: "#fff", borderColor: "#fff" },
        }}
        error={!!referrerError}
        helperText={
          referrerError ? (
            <span style={{ color: "#f44336" }}>{referrerError}</span>
          ) : (
            "Optional: who referred you (e.g., 0.0.12345)."
          )
        }
      />

      {/* Referrer validation + Mirror status */}
      {normalizedReferrerId && !referrerError && (
        <>
          <Typography variant="body2" color="white" align="center">
            Normalized account ID: {normalizedReferrerId}
          </Typography>

          {isMirrorChecking && (
            <Typography color="white" align="center">
              Checking account on Hedera testnet...
            </Typography>
          )}

          {!isMirrorChecking && isValidMirrorAccount === false && (
            <Typography color="error" align="center">
              Account not found on Hedera testnet.
            </Typography>
          )}

          {!isMirrorChecking && isValidMirrorAccount && (
            <Typography color="lightgreen" align="center">
              ✓ Valid Hedera account (testnet)
            </Typography>
          )}

          {!isMirrorChecking && evmAddress && (
            <Typography
              color="white"
              align="center"
              sx={{ wordBreak: "break-all", mt: 1 }}
            >
              EVM Address:
              <br />
              {evmAddress}
            </Typography>
          )}
        </>
      )}

      {!referrerInput && (
        <Typography color="white" align="center">
          You can paste a Hedera account ID above, or use{" "}
          <code>?id=0.0.x</code> in the URL to pre-fill it.
        </Typography>
      )}

      {/* Drill dApp UI (still here, just gated off for now) */}
      {SHOW_DRILL_APP && (
        <>
          {!accountId ? (
            // --- NOT CONNECTED ---
            <>
              <Typography variant="h4" color="white" align="center">
                {isDepositPhase ? "Deposit Tdrill" : "Withdraw Tdrill"}
              </Typography>
              <Typography color="white">
                {isDepositPhase
                  ? "To connect and deposit Tdrill (0.0.9484420), you need to be associated with USDC (0.0.456858) and Ttrader (0.0.9740604), and you need to give the contract (0.0.10079751) an allowance to spend your Tdrill."
                  : "Connect and withdraw your Tdrill over the weekend."}
              </Typography>
            </>
          ) : isDepositPhase ? (
            // --- DEPOSIT PHASE ---
            <>
              <Typography variant="h4" color="white" align="center">
                Deposit Tdrill
              </Typography>
              {loading ? (
                <Typography color="white">Checking status...</Typography>
              ) : depositSuccess ? (
                <>
                  <Typography color="white">Deposit successful!</Typography>
                  <Button
                    variant="contained"
                    onClick={() => setDepositSuccess(false)}
                  >
                    Back
                  </Button>
                </>
              ) : userTdrillBalance === 0 ? (
                <Typography color="error">
                  You have no Tdrill ({tdrillTokenId}) in your wallet to
                  deposit.
                </Typography>
              ) : !isUsdcAssociated ? (
                <Typography color="error">
                  You must be associated with USDC ({usdcTokenId}) to deposit.
                </Typography>
              ) : allowance === 0 ? (
                <Typography color="error">
                  You have not yet given the contract ({contractId}) an
                  allowance to spend your Tdrill.
                </Typography>
              ) : (
                // --- DEPOSIT FORM ---
                <>
                  <Typography color="white">
                    Your Tdrill Allowance: {allowance.toLocaleString()}
                    <br />
                    Your Tdrill Balance: {userTdrillBalance.toLocaleString()}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={`Amount of Tdrill (${tdrillTokenId}) to Deposit`}
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    InputLabelProps={{
                      style: { color: "#fff" },
                    }}
                    InputProps={{
                      style: { color: "#fff", borderColor: "#fff" },
                    }}
                    error={!!depositError}
                    helperText={
                      <span style={{ color: "#f44336" }}>{depositError}</span>
                    }
                  />
                  <Button
                    variant="contained"
                    onClick={handleDeposit}
                    disabled={getDepositButtonDisabled()}
                    fullWidth
                  >
                    Deposit Tdrill
                  </Button>
                </>
              )}
            </>
          ) : (
            // --- WITHDRAW PHASE ---
            <>
              <Typography variant="h4" color="white" align="center">
                Withdraw Tdrill
              </Typography>
              {loading ? (
                <Typography color="white">Checking status...</Typography>
              ) : success ? (
                <>
                  <Typography color="white">
                    Success! Check your wallet to see your Tdrill is back!
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSuccess(false);
                      checkStatus();
                    }}
                  >
                    Back to dApp
                  </Button>
                </>
              ) : !isTdrillAssociated ? (
                <Typography color="error">
                  You must be associated with Tdrill ({tdrillTokenId}) to use
                  the withdraw feature.
                </Typography>
              ) : userDepositedBalance === BigInt(0) ? (
                <Typography color="error">
                  You have no Tdrill balance in the contract to withdraw.
                </Typography>
              ) : !contractHasTdrill ? (
                <Typography color="error">
                  Contract has no Tdrill to withdraw.
                </Typography>
              ) : (
                // --- WITHDRAW BUTTON ---
                <>
                  <Button variant="contained" onClick={handleWithdraw}>
                    Withdraw Your {userDepositedBalance.toString()} Tdrill
                  </Button>
                  <Typography color="white" textAlign="center" mt={4}>
                    By calling this contract ({contractId}) you should receive
                    all your Tdrill back.
                  </Typography>
                </>
              )}
            </>
          )}

          <Typography color="white">
            Need help? See the{" "}
            <a
              href="https://tozm.io/?page=drillinstructions2"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#90caf9" }}
            >
              detailed instructions
            </a>
            .
            <br />
            <br />
            By using this dApp you signify your agreement to our{" "}
            <a
              href="https://tozm.io/?page=terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#90caf9" }}
            >
              Terms
            </a>
            .
          </Typography>
        </>
      )}
    </Stack>
  );
}
